import { List, Map } from "immutable";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { StateType as State } from "common/types";

import {
  clearPageValuesFromDataLayer,
  DATA_LAYER_SCOPES,
  pushPageSelectorOutputsToDataLayer
} from "seneca-common/features/analytics";

import useCache from "../useCache";

export { DATA_LAYER_SCOPES };

type Selectors = Record<string, (state: any) => any>;

export default function usePushPageSelectorOutputsToAnalyticsDataLayer(
  dataLayerScope: string,
  selectors: Selectors
) {
  // We use a cache to store the last values we pushed to the data layer
  // we use this to only push changes when a value has changed.
  const { cache } = useCache();

  const selectorsWithErrorSwallowed = swallowAllSelectorsErrors(selectors);

  const pushValuesToDataLayerSelector = pushPageSelectorOutputsToDataLayer(
    dataLayerScope,
    selectorsWithErrorSwallowed,
    cache
  );

  // This is actually more of an action as we need state to actually push the outputs to the data layer
  // not sure why exactly, but if you use 'useDispatch' and make it a thunk this will run less frequently
  // which means that we're more likely to have stale data
  useSelector(pushValuesToDataLayerSelector);

  useEffect(() => {
    return () => {
      clearPageValuesFromDataLayer({
        [dataLayerScope]: selectors
      });
    }; // dataLayerScope and selectors values shouldn't change, but selectors is an Object (whos reference may change each render) and we only want to run this cleanup on unmount
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
}
// Functional utils to wrap the selectors object

export function pushArgsToAllSelectors<ArgsT extends any[]>(
  args: ArgsT,
  selectors: Record<string, (state: any, ...args: ArgsT) => any>
) {
  return Object.keys(selectors).reduce((acc, name: string) => {
    return { ...acc, [name]: (state: any) => selectors[name](state, ...args) };
  }, {});
}

export function swallowAllSelectorsErrors(
  selectors: Record<string, (state: State) => any>
) {
  return Object.keys(selectors).reduce((acc, name: string) => {
    return {
      ...acc,
      [name]: (...args: any[]) => {
        try {
          // @ts-ignore
          return selectors[name](...args);
        } catch (err: any) {}
      }
    };
  }, {});
}

type Immutable<T> = List<T> | Map<string, T>;
type ImmutableSelector = (
  state: any,
  ...args: any[]
) => Immutable<any> | null | undefined;
export function selectorToJS<SelectorT extends ImmutableSelector>(
  selector: SelectorT
): SelectorT {
  // @ts-ignore - we're not going to return an immutable object here but it means we keep type guarding on the inputs
  return (state: State, ...args: any[]) => selector(state, ...args).toJS();
}
